import ClientLayout from "../../components/Layout/ClientLayout";
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { IoLogoInstagram } from "react-icons/io";
import { FaFacebookF, FaTwitter } from "react-icons/fa";
import ContactUsImage from "../../assets/images/AboutUs.jpg";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Send, Mountain, Sparkles, ArrowRight } from "lucide-react";

const ContactUs = () => {
  const handleSendEmail = () => {
    const to = "info@mambahikers.com";
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}`;
    window.open(gmailUrl, "_blank");
  };

  return (
    <ClientLayout>
      <div>
        {/* HERO SECTION */}
        <div className="relative h-[500px] lg:h-[600px] bg-about-us bg-cover bg-center overflow-hidden">
          {/* Gradient Overlay */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/90 to-mamba-sky-blue/80"
          />
          
          {/* Floating Mountain Icons */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                style={{
                  left: `${Math.random() * 90}%`,
                  top: `${Math.random() * 90}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  rotate: [0, 15, 0],
                }}
                transition={{
                  duration: 4 + i,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <Mountain className="w-16 h-16 text-white" />
              </motion.div>
            ))}
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-5">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-mamba-golden/20 backdrop-blur-sm rounded-full border border-mamba-golden/40 mb-6"
            >
              <MessageCircle className="w-4 h-4 text-mamba-golden" />
              <span className="text-sm font-bold font-montserrat text-mamba-golden uppercase tracking-wider">Let's Connect</span>
            </motion.div>

            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-5xl md:text-6xl lg:text-7xl font-black font-montserrat text-white mb-6 leading-tight"
            >
              Get In <span className="italic font-dancing-script text-mamba-golden">Touch</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-lg md:text-xl text-white/90 font-inter max-w-3xl"
            >
              Have questions about your next adventure? We're here to help you plan the perfect hiking experience.
            </motion.p>

            {/* Scroll Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, y: [0, 10, 0] }}
              transition={{ 
                opacity: { delay: 1, duration: 0.6 },
                y: { duration: 2, repeat: Infinity, ease: "easeInOut" }
              }}
              className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
            >
              <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
                <motion.div 
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Contact Information Section */}
        <section className="px-5 md:px-10 lg:px-20 xl:px-32 py-16 lg:py-24 bg-gradient-to-br from-white via-mamba-bg-light to-white">
          <div className="max-w-8xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Contact Cards */}
              <div className="space-y-6">
                {/* Phone */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  whileHover={{ y: -4 }}
                  className="group relative bg-gradient-to-br from-white to-mamba-bg-light rounded-3xl p-8 border-2 border-mamba-navy/20 hover:border-mamba-golden shadow-lg hover:shadow-2xl transition-all overflow-hidden"
                >
                  {/* Decorative element */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-mamba-navy/10 to-transparent rounded-full blur-3xl group-hover:scale-150 transition-transform" />
                  
                  <div className="relative flex items-start gap-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-mamba-navy to-slate-700 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold font-montserrat text-xl text-mamba-navy mb-2">Call Us</h4>
                      <p className="text-lg font-inter text-slate-700 font-semibold">+254 714 818 723</p>
                      <p className="text-sm font-inter text-mamba-navy/60 mt-1">
                        Mon-Fri: 8AM - 6PM EAT
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Email */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  whileHover={{ y: -4 }}
                  className="group relative bg-gradient-to-br from-white to-mamba-bg-light rounded-3xl p-8 border-2 border-mamba-sky-blue/20 hover:border-mamba-golden shadow-lg hover:shadow-2xl transition-all overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-mamba-sky-blue/10 to-transparent rounded-full blur-3xl group-hover:scale-150 transition-transform" />
                  
                  <div className="relative flex items-start gap-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-mamba-sky-blue to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold font-montserrat text-xl text-mamba-navy mb-2">Email Us</h4>
                      <motion.button
                        whileHover={{ x: 2 }}
                        onClick={handleSendEmail}
                        className="text-lg font-inter text-mamba-sky-blue hover:text-mamba-navy font-semibold transition-colors flex items-center gap-1"
                      >
                        info@mambahikers.com
                        <Send className="w-4 h-4" />
                      </motion.button>
                      <p className="text-sm font-inter text-mamba-navy/60 mt-1">
                        We'll respond within 24 hours
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Location */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  whileHover={{ y: -4 }}
                  className="group relative bg-gradient-to-br from-white to-mamba-bg-light rounded-3xl p-8 border-2 border-mamba-golden/30 hover:border-mamba-golden shadow-lg hover:shadow-2xl transition-all overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-mamba-golden/10 to-transparent rounded-full blur-3xl group-hover:scale-150 transition-transform" />
                  
                  <div className="relative flex items-start gap-4">
                    <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-mamba-golden to-amber-400 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <MapPin className="w-6 h-6 text-mamba-navy" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold font-montserrat text-xl text-mamba-navy mb-2">Visit Us</h4>
                      <p className="text-lg font-inter text-slate-700 font-semibold">Nairobi, Kenya</p>
                      <p className="text-sm font-inter text-mamba-navy/60 mt-1">
                        J's Arcade, Thome Outlet Number J28
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Social Media & WhatsApp Section */}
              <div className="space-y-6">
                {/* Social Media */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="bg-gradient-to-br from-white to-mamba-bg-light rounded-3xl p-8 border-2 border-mamba-sky-blue/20 hover:border-mamba-golden shadow-lg hover:shadow-2xl transition-all"
                >
                  <h4 className="font-bold font-montserrat text-2xl text-mamba-navy mb-3">Follow Our <span className="italic font-dancing-script text-mamba-sky-blue">Journey</span></h4>
                  <p className="text-mamba-navy/70 font-inter mb-6">
                    Stay updated with our latest adventures and hiking tips!
                  </p>
                  <div className="flex gap-4">
                    <motion.a
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://www.instagram.com/mambahikers?igsh=MWNwZXQweDd6ODdoZg=="
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <IoLogoInstagram size={28} color="white" />
                    </motion.a>
                    <motion.a
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://www.facebook.com/mambahikers"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded-full bg-blue-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <FaFacebookF size={24} color="white" />
                    </motion.a>
                    <motion.a
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://x.com/Mambahikers?t=zN1p261-8xzi5ERoSEFNoA&s=09"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-14 h-14 rounded-full bg-sky-500 flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
                    >
                      <FaTwitter size={24} color="white" />
                    </motion.a>
                  </div>
                </motion.div>

                {/* WhatsApp CTA */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="relative bg-gradient-to-br from-green-500 to-green-600 rounded-3xl p-8 shadow-2xl overflow-hidden"
                >
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                  
                  <div className="relative">
                    <MessageCircle className="w-12 h-12 text-white mb-4" />
                    <h4 className="font-bold font-montserrat text-2xl text-white mb-3">Quick Response?</h4>
                    <p className="text-white/90 font-inter mb-6">
                      Chat with us on WhatsApp for instant replies!
                    </p>
                    <motion.a
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://wa.me/254714818723"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-white text-green-600 font-bold font-montserrat py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all"
                    >
                      Chat Now
                      <ArrowRight className="w-5 h-5" />
                    </motion.a>
                  </div>
                </motion.div>

                {/* Image Card */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="relative rounded-3xl overflow-hidden shadow-2xl group"
                >
                  <img
                    src={ContactUsImage}
                    alt="Mamba Hikers Adventure"
                    className="w-full h-80 lg:h-96 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/80 via-mamba-navy/40 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="text-white font-montserrat text-xl font-bold">Adventure Awaits</p>
                    <p className="text-white/90 font-inter text-sm">Join us on the trails</p>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA SECTION */}
        <section className="px-5 md:px-10 lg:px-20 xl:px-32 py-16 lg:py-24 bg-gradient-to-br from-mamba-navy via-slate-900 to-mamba-sky-blue">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-mamba-golden/20 backdrop-blur-sm rounded-full border border-mamba-golden/40 mb-6"
            >
              <Sparkles className="w-4 h-4 text-mamba-golden" />
              <span className="text-sm font-bold font-montserrat text-mamba-golden uppercase tracking-wider">Start Your Journey</span>
            </motion.div>

            <motion.h3
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-5xl lg:text-6xl font-black font-montserrat text-white mb-6 leading-tight"
            >
              Ready for Your Next <span className="italic font-dancing-script text-mamba-golden">Adventure?</span>
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-lg md:text-xl text-white/90 font-inter mb-10 max-w-2xl mx-auto"
            >
              Join thousands of adventurers who have discovered the thrill of
              hiking with Mamba Hikers. Let's make your mountain dreams a reality!
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => (window.location.href = "/events")}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-mamba-golden to-amber-400 text-mamba-navy font-bold font-montserrat py-4 px-8 rounded-full shadow-2xl hover:shadow-mamba-golden/50 transition-all"
              >
                Browse Events
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSendEmail}
                className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white font-bold font-montserrat py-4 px-8 rounded-full hover:bg-white hover:text-mamba-navy transition-all"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </motion.button>
            </motion.div>
          </div>
        </section>
      </div>
    </ClientLayout>
  );
};

export default ContactUs;
